import React from "react";
import * as RegionSelector from "../RegionSelector_v2";
import { AsyncData, AsyncDataHelpers, asyncData } from "../utils/AsyncData";
import { maybe } from "../utils/Maybe";
import { bigWarning } from "../utils/warning";
import { MaybeEntityFormState, transformDataToMaybeEntityFormState } from "./EntityFormState";
import { EqualityCheckCounter } from "./EqualityCheckCounter";

type UseDataHookParameter<Extra> = {
  data: {
    resourceLocation: string | null;
    vpc: string | null;
  };
  extra: Extra;
  helpers: {
    async: AsyncDataHelpers;
    location: RegionSelector.Types.LocationHelpers;
  };
};

type AsyncDataWithOptions<T extends { value: string }> = AsyncData<{ options: T[]; defaultValue?: string | null }>;
type AsyncDataWithoutOptions = AsyncData<{ options: []; defaultValue?: null }>;

type UseDataHookResult = {
  resourceLocations?: AsyncDataWithOptions<RegionSelector.Types.ResourceLocation> | AsyncDataWithoutOptions;
  vpcs?: AsyncDataWithOptions<RegionSelector.Types.VPC> | AsyncDataWithoutOptions;
};

type UseDataHook<Extra> = (param: UseDataHookParameter<Extra>) => UseDataHookResult;

type HTMLInputNames = {
  resourceLocation: string;
  vpc: string;
};

type HookParameterWithoutExtra = {
  htmlInputNames?: Partial<HTMLInputNames>;
};

type HookParameterWithExtra<Extra> = {
  htmlInputNames?: Partial<HTMLInputNames>;
  extra: Extra;
};

type ExtraStateData = {
  name: string;
  onSelect(value: string): void;
};

export type CreatePageRegionSelectorState = {
  resourceLocations: AsyncData<MaybeEntityFormState<RegionSelector.Types.ResourceLocation, ExtraStateData>>;
  vpcs: AsyncData<MaybeEntityFormState<RegionSelector.Types.VPC, ExtraStateData>>;
  values: {
    resourceLocation: string | null;
    vpc: string | null;
  };
  valid: boolean;
};

const useOptionsEqualityCheck = <T>(currentValue: AsyncData<{ options: T[] }>, debugKey: string) => {
  const instance = React.useMemo(() => {
    const warn = () => {
      bigWarning(
        `The callback provided to CreatePage.createRegionSelectorState is frequently returning a new options array on every render. This is a major performance hit. Make sure that ${debugKey}.options does not change between renders unless the data changes. ` +
          `If you are using \`helpers.location.allDatacenters\` or \`helpers.location.allRegions\`, make sure that you are: memoizing the input to these functions; and memoize any filter/map you perform on the result.`,
        "useOptionsEqualityCheck"
      );
    };

    return new EqualityCheckCounter(warn, 5);
  }, []);

  if (asyncData.is.loading(currentValue) || asyncData.is.error(currentValue)) {
    instance.reset();
    return;
  }

  /**
   * If there are no options then we don't need to check because they will be removed from the state anyway.
   */
  if (currentValue.data.options.length === 0) {
    return;
  }

  instance.check(currentValue.data.options);
};

const helpers = {
  async: asyncData,
  location: RegionSelector.locationHelpers,
};

const deref = (wrapper: AsyncData<MaybeEntityFormState<any, unknown>>): { selected: string | null; valid: boolean } => {
  if (asyncData.is.ready(wrapper) && maybe.is.some(wrapper.data)) {
    const value = wrapper.data.value;

    return {
      selected: value.selected,
      valid: value.options.length > 0 ? value.selected !== null : true,
    };
  }

  return {
    selected: null,
    valid: true,
  };
};

const nothing: any[] = [];
const nullResourceLocations = helpers.async.ready({ options: nothing });
const nullVpcs = helpers.async.ready({ options: nothing });

/**
 * A factory function that creates a hook `useCreatePageRegionSelectorState` to be used in conjunction with `CreatePage.RegionSelector`.
 * It requires a hook as a parameter which is used to fetch data for the components.
 *
 * **THE OPTIONS ARRAY FOR BOTH `resourceLocations` AND `vpcs` MUST REMAIN REFERENTIALLY EQUAL BETWEEN RENDERS UNLESS DATA HAS CHANGED.**
 */

export function createCreatePageRegionSelectorState(
  useData: UseDataHook<void>
): (param?: HookParameterWithoutExtra) => CreatePageRegionSelectorState;

export function createCreatePageRegionSelectorState<Extra>(
  useData: UseDataHook<Extra>
): (param: HookParameterWithExtra<Extra>) => CreatePageRegionSelectorState;

export function createCreatePageRegionSelectorState(useData: UseDataHook<any>) {
  return (param: HookParameterWithExtra<any> = { extra: undefined }): CreatePageRegionSelectorState => {
    const { htmlInputNames = {}, extra = undefined } = param;
    const [resourceLocation, setResourceLocation] = React.useState<string | null>(null);
    const [vpc, setVPC] = React.useState<string | null>(null);

    const result = useData({
      data: { resourceLocation, vpc },
      extra,
      helpers,
    });

    const asyncResourceLocations = helpers.async.useCachedRef(
      result.resourceLocations ?? (nullResourceLocations as AsyncDataWithOptions<RegionSelector.Types.ResourceLocation>)
    );
    const asyncVpcs = helpers.async.useCachedRef(result.vpcs ?? (nullVpcs as AsyncDataWithOptions<RegionSelector.Types.VPC>));

    /**
     * This does not break the rules of hooks because `process.env.NODE_ENV` will not change in between renders.
     */
    if (process.env.NODE_ENV !== "production") {
      useOptionsEqualityCheck(asyncResourceLocations, "resourceLocations");
      useOptionsEqualityCheck(asyncVpcs, "vpcs");
    }

    const resourceLocations = React.useMemo(() => {
      return helpers.async.map(asyncResourceLocations, (next) =>
        transformDataToMaybeEntityFormState(next, {
          currentValue: resourceLocation,
          debugKey: "resourceLocations",
          extra: {
            name: htmlInputNames.resourceLocation ?? "resourceLocation",
            onSelect: setResourceLocation,
          },
        })
      );
    }, [asyncResourceLocations, resourceLocation, htmlInputNames.resourceLocation]);

    const vpcs = React.useMemo(() => {
      return helpers.async.map(asyncVpcs, (next) =>
        transformDataToMaybeEntityFormState(next, {
          currentValue: vpc,
          debugKey: "vpcs",
          extra: {
            name: htmlInputNames.vpc ?? "vpc",
            onSelect: setVPC,
          },
        })
      );
    }, [asyncVpcs, vpc, htmlInputNames.vpc]);

    return React.useMemo(() => {
      const derefResourceLocations = deref(resourceLocations);
      const derefVpcs = deref(vpcs);

      return {
        resourceLocations,
        vpcs,
        values: {
          resourceLocation: derefResourceLocations.selected,
          vpc: derefVpcs.selected,
        },
        valid: derefResourceLocations.valid && derefVpcs.valid,
      };
    }, [resourceLocations, vpcs]);
  };
}
